/**
 * Sign-in.
 *
 * Three decisions shape this screen.
 *
 * The second factor is a **step**, not a field that appears under the password.
 * A code box grafted onto a form the operator has already filled in reads as an
 * afterthought and leaves three inputs on screen when only one of them is being
 * asked for. Once the server says the password was accepted, the card becomes a
 * verification panel: who is signing in, the code, and the way out if the
 * authenticator is gone. The code entry is a single real input drawn as six
 * cells — one value, one label, one caret — rather than six inputs, because six
 * inputs is six accessible names, six focus handlers and a paste that has to be
 * torn apart by hand.
 *
 * **Keep me signed in** reaches the server. It is a field on the login request
 * and a lifetime on the session it mints (ADR-0031), not a preference this page
 * remembers — a box that never left the browser would be a control that appears
 * to work and does nothing, which is invisible from every screen. Its hint names
 * no number of days, because the lifetime is server configuration and this page
 * is unauthenticated; the real date shows in the session list, where it is a
 * fact rather than a promise.
 *
 * Rate limiting is the other interesting state. A locked-out operator needs to
 * know it is a lockout and not a wrong password, and needs to know when it
 * lifts — so the countdown is live and the submit button stays disabled until
 * it reaches zero. Guessing repeatedly against a closed door is not a state the
 * interface should permit.
 */

import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react'
import clsx from 'clsx'
import { LifeBuoy, ShieldCheck, UserRound } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import { Button } from '@/components/primitives'
import { CheckboxField, TextField, Notice } from '@/components/controls'
import { Icon } from '@/components/icon'
import { Logo } from '@/components/Logo'
import { ClassificationBanner } from '@/components/ClassificationBanner'
import { TacticalBackdrop } from '@/components/TacticalBackdrop'
import { useT } from '@/i18n/context'
import './LoginPage.css'

/** The number of digits the server mints. `auth.DefaultTOTPConfig` is six, and
 *  the deployment does not override it (`cmd/kimene-server/main.go`). */
const CODE_LENGTH = 6
const CELLS = [0, 1, 2, 3, 4, 5]

/** A recovery code is twenty Crockford-base32 characters, shown in four groups
 *  of five. The server normalises whatever is typed, so the grouping here is
 *  for the eye — a code copied off paper should look on screen like it does on
 *  paper. */
const RECOVERY_LENGTH = 20

/** Which of the two second factors the operator is presenting. Both are
 *  accepted by `POST /auth/login`; only the authenticator had a control. */
type Factor = 'totp' | 'recovery'

interface FieldErrors {
  username?: string
  password?: string
  code?: string
  recovery?: string
}

export function LoginPage({ onAuthenticated }: { onAuthenticated: () => void }) {
  const t = useT()
  const titleId = useId()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)

  const [secondFactor, setSecondFactor] = useState(false)
  const [factor, setFactor] = useState<Factor>('totp')
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState('')
  // Re-keys the code entry, which clears it and takes focus again without a ref
  // reaching across the tree. After a refusal the operator's next keystroke
  // should begin a code, not append to the one that was just refused.
  const [attempt, setAttempt] = useState(0)

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [lockSeconds, setLockSeconds] = useState(0)
  const [busy, setBusy] = useState(false)

  // The lockout countdown runs in the page rather than being re-read from the
  // server, so the operator watches it fall instead of retrying to find out.
  useEffect(() => {
    if (lockSeconds <= 0) return
    const timer = setInterval(() => setLockSeconds((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(timer)
  }, [lockSeconds])

  const locked = lockSeconds > 0

  async function submit(e?: FormEvent, codeOverride?: string) {
    e?.preventDefault()
    if (busy || locked) return

    const typedCode = (codeOverride ?? code).trim()
    const typedRecovery = recovery.trim()

    const nextErrors: FieldErrors = {}
    if (!secondFactor) {
      if (!username.trim()) nextErrors.username = t('auth.usernameRequired')
      if (!password) nextErrors.password = t('auth.passwordRequired')
    } else if (factor === 'totp') {
      // Two messages, because they are two different mistakes. "This account
      // wants a code" is right for an empty field and wrong for three digits,
      // where what the operator needs to be told is how many are missing.
      if (typedCode.length === 0) nextErrors.code = t('auth.totpRequired')
      else if (typedCode.length < CODE_LENGTH) nextErrors.code = t('auth.totpIncomplete')
    } else if (!typedRecovery) {
      nextErrors.recovery = t('auth.recoveryRequired')
    }
    setFieldErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setBusy(true)
    setFormError(null)
    try {
      // The response is discarded on purpose. `api.LoginResponse` carries the
      // actor, the CSRF token, the expiry, `mustChangePassword` and
      // `recoveryCodesRemaining`; the session and CSRF cookies arrive as
      // cookies, and the shell re-reads the rest from `/auth/session` the
      // moment it mounts. Typing a body nothing reads is how a field ends up
      // declared, translated and rendered by nobody.
      //
      // The two flags are a real gap and predate this screen: a password that
      // must be changed, and a recovery pool running low, both go unmentioned.
      await api.post<unknown>('/auth/login', {
        username: username.trim(),
        password,
        // These four names are the server's, not this file's
        // (`api.LoginRequest`). The body said `totp` once while the server read
        // `totpCode`, so a second factor could be typed correctly and still be
        // reported as missing — sign-in with TOTP enrolled was simply
        // impossible, and every test on both sides passed. The names are now
        // pinned from the far end too, by
        // `TestTheLoginBodyTheBrowserSendsIsTheOneTheServerReads`, which decodes
        // exactly this shape.
        totpCode: factor === 'totp' ? typedCode || undefined : undefined,
        recoveryCode: factor === 'recovery' ? typedRecovery || undefined : undefined,
        remember,
      })
      onAuthenticated()
    } catch (err) {
      setBusy(false)
      if (!(err instanceof ApiError)) {
        setFormError(t('error.unexpected'))
        return
      }
      if (err.status === 429) {
        const retryAfter = Number(err.fields?.retryAfter ?? '60')
        setLockSeconds(Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : 60)
        return
      }
      if (err.code === 'totp_required') {
        // The password has been accepted. Nothing from here is about it.
        setSecondFactor(true)
        setFieldErrors({})
        setAttempt((n) => n + 1)
        return
      }
      if (err.status === 0) {
        setFormError(t('error.network'))
        return
      }
      if (err.status === 401 || err.status === 403) {
        // Once the server has asked for a second factor the password is known
        // good, so a refusal from here is the code — and naming the password
        // would send the operator to re-check the one thing that is not wrong.
        // The API collapses a bad code into the generic unauthenticated code on
        // purpose, so that it refuses to be an oracle; the distinction is made
        // from the state this form already holds rather than by asking the
        // server to leak it.
        if (!secondFactor) {
          setFormError(t('auth.invalidCredentials'))
          return
        }
        if (factor === 'totp') {
          setCode('')
          setFieldErrors({ code: t('auth.totpInvalid') })
        } else {
          setRecovery('')
          setFieldErrors({ recovery: t('auth.recoveryInvalid') })
        }
        setAttempt((n) => n + 1)
        return
      }
      setFormError(err.message || t('error.unexpected'))
    }
  }

  /** Six digits is the whole code. Asking the operator to then reach for a
   *  button is a step that exists only because the form could not tell it was
   *  finished. */
  function onCode(next: string) {
    const digits = next.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    if (fieldErrors.code) setFieldErrors({})
    if (digits.length === CODE_LENGTH) void submit(undefined, digits)
  }

  function backToCredentials() {
    setSecondFactor(false)
    setFactor('totp')
    setCode('')
    setRecovery('')
    setFieldErrors({})
    setFormError(null)
  }

  function switchFactor(next: Factor) {
    setFactor(next)
    setCode('')
    setRecovery('')
    setFieldErrors({})
    setFormError(null)
    setAttempt((n) => n + 1)
  }

  return (
    <main id="main" className="k-login" tabIndex={-1}>
      <TacticalBackdrop />
      <ClassificationBanner />
      <div className="k-login__grid">
        {/* The stage is a decorative showcase; a screen reader should land on
            the form, not read a marketing column first. Everything inside it is
            therefore non-interactive as well as hidden — a focusable element
            under aria-hidden is a tab stop nobody can hear. */}
        <section className="k-login__stage" aria-hidden="true">
          <div className="k-login__pitch">
            <p className="k-login__eyebrow k-eyebrow">
              <span className="k-label">{t('auth.heroEyebrow')}</span>
            </p>
            <p className="k-login__headline">{t('auth.heroTitle')}</p>
            <p className="k-login__lede">{t('auth.heroLede')}</p>
          </div>
          <DispatchPlate />
          <p className="k-login__thesis">{t('auth.heroThesis')}</p>
        </section>

        <div className="k-login__panel">
          <form
            className="k-login__card k-frame"
            onSubmit={(e) => void submit(e)}
            aria-labelledby={titleId}
            noValidate
          >
            <header className="k-login__brand">
              <Logo className="k-login__mark" size={34} />
              <div className="k-login__brandtext">
                <span className="k-login__kicker k-label">
                  {secondFactor ? t('auth.secondFactor') : t('auth.operator')}
                </span>
                <h1 className="k-login__title" id={titleId}>
                  {t('auth.signIn')}
                </h1>
              </div>
            </header>

            {/* aria-live because these appear after a submit the operator has
                already turned their attention away from. */}
            <div className="k-login__alerts" aria-live="polite">
              {locked && (
                <Notice tone="err">{t('auth.rateLimited', { seconds: lockSeconds })}</Notice>
              )}
              {!locked && formError && <Notice tone="err">{formError}</Notice>}
            </div>

            {secondFactor ? (
              <>
                <div className="k-login__whoami">
                  <Icon icon={UserRound} size={14} className="k-login__whoami-icon" />
                  <span className="k-login__whoami-name k-mono">{username.trim()}</span>
                  <button type="button" className="k-login__link" onClick={backToCredentials}>
                    {t('action.back')}
                  </button>
                </div>

                {factor === 'totp' ? (
                  <>
                    <CodeField
                      key={attempt}
                      label={t('auth.totp')}
                      hint={t('auth.totpHint')}
                      error={fieldErrors.code}
                      value={code}
                      onChange={onCode}
                      disabled={busy || locked}
                    />
                    <SubmitRow busy={busy} locked={locked} />
                    <button
                      type="button"
                      className="k-login__link k-login__link--foot"
                      onClick={() => switchFactor('recovery')}
                    >
                      <Icon icon={LifeBuoy} size={13} />
                      {t('auth.useRecoveryCode')}
                    </button>
                  </>
                ) : (
                  <>
                    <TextField
                      key={attempt}
                      label={t('auth.recoveryCode')}
                      hint={t('auth.recoveryCodeHint')}
                      error={fieldErrors.recovery}
                      value={recovery}
                      onChange={(v) => setRecovery(groupRecoveryCode(v))}
                      autoComplete="one-time-code"
                      // Not a translated string: it is the shape the server
                      // printed the code in, and it reads the same in every
                      // language. The sentence explaining it is the hint.
                      placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                      mono
                      autoFocus
                      name="recoveryCode"
                      disabled={busy || locked}
                    />
                    <SubmitRow busy={busy} locked={locked} />
                    <button
                      type="button"
                      className="k-login__link k-login__link--foot"
                      onClick={() => switchFactor('totp')}
                    >
                      <Icon icon={ShieldCheck} size={13} />
                      {t('auth.useAuthenticator')}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <TextField
                  label={t('auth.username')}
                  value={username}
                  onChange={(v) => setUsername(v)}
                  autoComplete="username"
                  required
                  autoFocus
                  name="username"
                  error={fieldErrors.username}
                />

                <TextField
                  label={t('auth.password')}
                  type="password"
                  value={password}
                  onChange={(v) => setPassword(v)}
                  autoComplete="current-password"
                  required
                  name="password"
                  error={fieldErrors.password}
                />

                <div className="k-login__remember">
                  <CheckboxField
                    label={t('auth.rememberMe')}
                    hint={t('auth.rememberMeHint')}
                    checked={remember}
                    onChange={setRemember}
                  />
                </div>

                <SubmitRow busy={busy} locked={locked} />
              </>
            )}

            <footer className="k-login__foot">{t('auth.serverNote')}</footer>
          </form>
        </div>
      </div>
    </main>
  )
}

/** The one submit button, shared by both steps.
 *
 *  Shared rather than written twice because its accessible name is load-bearing
 *  in `LoginPage.test.tsx` and in `e2e/accessibility.spec.ts`: a second button
 *  on the card that also read "Oturum aç" would make every
 *  `getByRole('button', { name: /oturum aç/i })` ambiguous rather than wrong,
 *  which fails with a message about the query and not about the screen. */
function SubmitRow({ busy, locked }: { busy: boolean; locked: boolean }) {
  const t = useT()
  return (
    <Button
      type="submit"
      variant="primary"
      className="k-login__submit"
      busy={busy}
      disabled={locked}
    >
      {busy ? t('auth.signingIn') : t('action.login')}
    </Button>
  )
}

/**
 * The verification code, drawn as cells over one real input.
 *
 * Six separate inputs is the obvious build and the wrong one: it produces six
 * accessible names for one value, needs its own focus choreography, and turns a
 * paste — the way a code actually arrives, from a phone or a password manager —
 * into something the page has to tear apart by hand. Here there is exactly one
 * `<input>`, carrying the label, the value, the `one-time-code` autofill hint
 * and the error wiring; the cells behind it are `aria-hidden` decoration that
 * happens to hold the digits.
 *
 * The input's text and caret are transparent, so the cells show through, and
 * the caret is drawn instead: the cell at the next empty position carries a
 * blinking bar while the field has focus. That bar is also the focus indicator,
 * which is why it is declared visible and only its blink is animated — under
 * `prefers-reduced-motion` the animation is cut to nothing and the bar must
 * still be there.
 */
function CodeField({
  label,
  hint,
  error,
  value,
  onChange,
  disabled,
}: {
  label: string
  hint?: string
  error?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const id = useId()
  const ref = useRef<HTMLInputElement>(null)
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined

  // The caret is invisible, so a click in the middle of the row would leave it
  // somewhere the operator cannot see and the next digit would land between two
  // others. Focus and click park it at the end; arrows and select-all are left
  // alone, so Ctrl+A followed by a paste still replaces the code.
  const toEnd = useCallback(() => {
    const el = ref.current
    if (!el) return
    const end = el.value.length
    if (el.selectionStart !== end || el.selectionEnd !== end) el.setSelectionRange(end, end)
  }, [])

  return (
    <div className="k-field">
      <label className="k-field__label" htmlFor={id}>
        {label}
      </label>
      <div className={clsx('k-otp', error && 'k-otp--invalid')}>
        <input
          ref={ref}
          id={id}
          name="totpCode"
          className="k-otp__input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={toEnd}
          onClick={toEnd}
          inputMode="numeric"
          autoComplete="one-time-code"
          // No maxLength. The browser applies it to a paste *before* the change
          // handler sees it, so a code pasted as "751 903" — which is how a
          // phone offers it — would arrive truncated to five digits and never
          // complete. The handler keeps the digits and stops at six instead.
          disabled={disabled}
          autoFocus
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
        <div className="k-otp__cells" aria-hidden="true">
          {CELLS.map((i) => (
            <span
              key={i}
              className={clsx(
                'k-otp__cell',
                value[i] !== undefined && 'k-otp__cell--on',
                i === value.length && 'k-otp__cell--next',
              )}
            >
              {value[i] ?? ''}
            </span>
          ))}
        </div>
      </div>
      {error ? (
        <p className="k-field__error" id={`${id}-err`}>
          {error}
        </p>
      ) : hint ? (
        <p className="k-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/** Regroups a recovery code as it is typed, in the shape the server printed it.
 *  Nothing is validated here — the alphabet excludes I, L, O and U precisely so
 *  a hand-copied code is unambiguous, and silently deleting a character the
 *  operator can see they typed would hide the mistake rather than show it. */
function groupRecoveryCode(raw: string): string {
  const chars = raw
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, RECOVERY_LENGTH)
  return (chars.match(/.{1,5}/g) ?? []).join('-')
}

/**
 * The dispatch plate: what Kimene does, drawn top to bottom in the order it
 * happens.
 *
 * Pools of candidates are considered; most are struck off at the eligibility
 * bar; the survivors converge on one decision; exactly one route leaves it and
 * lands on one host, which is the only lit pane in a wall of three. That last
 * detail is the honest one — a control plane running an agent is one live
 * terminal and a lot of idle capacity, and a hero that lit all three would be
 * selling something the product does not do.
 *
 * It claims no data. There is not a number anywhere on it, because this is the
 * one screen where the system genuinely knows nothing yet, and a dashboard full
 * of invented figures at the gate would contradict "completion requires
 * evidence" in the first three seconds. The single label attached to a value is
 * the word for *unmeasured*, drawn in the unknown colour and hatched — because
 * unknown is not zero, and this is where the interface says so first.
 *
 * Decorative, so it is hidden from assistive technology; every signal it
 * carries is position, colour or fill, so `prefers-reduced-motion` removes the
 * tempo and none of the meaning.
 */
function DispatchPlate() {
  const t = useT()
  // Five pools, four anonymous candidates each. Nothing here is a record of
  // anything — the chips carry no identity, because the plate is a drawing of
  // the act and not a reading of the fleet.
  const pools = [
    { x: 56, label: t('auth.routeAccount') },
    { x: 148, label: t('auth.routeModel') },
    { x: 240, label: t('auth.routeHost') },
    { x: 332, label: t('auth.routeProject') },
    { x: 424, label: t('auth.routeRole') },
  ]
  // Twelve candidates reach the gate and five pass it. The proportion is the
  // point: routing is mostly refusal, and the refusals are what the decision
  // record exists to explain.
  const candidates = [
    { x: 40, live: false },
    { x: 56, live: true },
    { x: 72, live: false },
    { x: 132, live: false },
    { x: 148, live: true },
    { x: 164, live: false },
    { x: 240, live: true },
    { x: 256, live: false },
    { x: 332, live: true },
    { x: 348, live: false },
    { x: 408, live: false },
    { x: 424, live: true },
  ]
  const hosts = [
    { x: 12, live: false },
    { x: 176, live: false },
    { x: 340, live: true },
  ]
  const outputs = [46, 74, 34, 88, 58, 40]
  // One string, two paths: the route, and the bright dash that runs it.
  const route = 'M240 254 C 240 310, 404 300, 404 368'

  return (
    <div className="k-plate">
      <svg
        className="k-plate__svg"
        viewBox="0 0 480 530"
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ---- the pools ---- */}
        {pools.map((p) => (
          <g key={p.label}>
            <text className="k-plate__cap" x={p.x} y={12} textAnchor="middle">
              {p.label}
            </text>
            {[0, 1, 2, 3].map((c) => (
              <rect
                key={c}
                className="k-plate__chip"
                x={p.x - 18}
                y={24 + c * 14}
                width={36}
                height={8}
                rx={2}
              />
            ))}
          </g>
        ))}

        {/* ---- the gate: seven candidates stop here, five go on ---- */}
        <text className="k-plate__cap k-plate__cap--start" x={20} y={106}>
          {t('auth.routeEligibility')}
        </text>
        <line className="k-plate__gate" x1={20} y1={116} x2={460} y2={116} />
        {candidates.map((c) =>
          c.live ? (
            <path
              key={c.x}
              className="k-plate__candidate"
              d={`M${c.x} 82 C ${c.x} 150, 240 160, 240 204`}
            />
          ) : (
            <g key={c.x} className="k-plate__reject">
              <line x1={c.x} y1={82} x2={c.x} y2={112} />
              <path d={`M${c.x - 4} 112 l8 8 M${c.x + 4} 112 l-8 8`} />
            </g>
          ),
        )}

        {/* ---- the decision, its weights, and the record it writes ---- */}
        {/* The weights the decision is made on, beside the node it feeds. They
            carry no numbers and no scale — the shape of the mechanism is a
            claim the product can back; a figure would not be. The third lane is
            not a short bar but an empty one: an input nobody has measured is
            not an input measured at zero. */}
        {[32, 54, 0, 44].map((w, i) => (
          <g key={i}>
            <rect className="k-plate__track" x={120} y={200 + i * 13} width={80} height={6} rx={3} />
            {w > 0 ? (
              <rect className="k-plate__fill" x={120} y={200 + i * 13} width={w} height={6} rx={3} />
            ) : (
              <rect
                className="k-plate__unmeasured"
                x={120.5}
                y={200.5 + i * 13}
                width={79}
                height={5}
                rx={2.5}
              />
            )}
          </g>
        ))}
        <text className="k-plate__cap k-plate__cap--unknown k-plate__cap--start" x={120} y={264}>
          {t('auth.routeUnmeasured')}
        </text>

        <g className="k-plate__record">
          <text className="k-plate__cap k-plate__cap--start" x={344} y={202}>
            {t('auth.routeRecord')}
          </text>
          {[0, 1, 2].map((i) => (
            <rect key={i} x={344 + i * 5} y={210 + i * 5} width={64} height={12} rx={2} />
          ))}
        </g>

        <circle className="k-plate__ring" cx={240} cy={228} r={24} />
        <rect
          className="k-plate__halo"
          x={225}
          y={213}
          width={30}
          height={30}
          rx={5}
          transform="rotate(45 240 228)"
        />
        <rect
          className="k-plate__node"
          x={231}
          y={219}
          width={18}
          height={18}
          rx={3}
          transform="rotate(45 240 228)"
        />
        <text className="k-plate__cap k-plate__cap--start" x={278} y={232}>
          {t('auth.routeDecision')}
        </text>

        {/* ---- one route leaves, and it is the only teal path on the plate ---- */}
        <path className="k-plate__route" d={route} />
        <path className="k-plate__token" d={route} />

        {/* ---- the wall: one pane is live, two are not ---- */}
        <text className="k-plate__cap k-plate__cap--start" x={20} y={352}>
          {t('auth.routeExec')}
        </text>
        {hosts.map((h) => (
          <g
            key={h.x}
            transform={`translate(${h.x} 362)`}
            className={h.live ? 'k-plate__pane--live' : undefined}
          >
            <rect className="k-plate__pane" x={0} y={0} width={128} height={150} rx={6} />
            <rect className="k-plate__panerail" x={0} y={19} width={128} height={1.5} />
            <rect className="k-plate__panechip" x={10} y={8} width={28} height={5} rx={2} />
            <rect className="k-plate__panechip" x={44} y={8} width={16} height={5} rx={2} />
            <rect className="k-plate__panescreen" x={8} y={28} width={112} height={114} rx={3} />
            {outputs.map((w, i) => (
              <rect
                key={i}
                className="k-plate__paneline"
                x={16}
                y={38 + i * 15}
                width={w}
                height={3}
                rx={1.5}
              />
            ))}
            {h.live && <rect className="k-plate__cursor" x={16} y={126} width={6} height={9} />}
          </g>
        ))}
        <text className="k-plate__cap k-plate__cap--on" x={404} y={526} textAnchor="middle">
          {t('auth.routeLive')}
        </text>
      </svg>
    </div>
  )
}
